import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import type { ColDef, GridApi, GridReadyEvent, IDatasource } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { localeText as defaultLocaleText } from '../../../constants/AgGridLocale';
import styles from './BaseAgGrid.module.css';

export type GridFetchParams = {
  startRow: number;
  endRow: number;
  filterModel: any;
  sortModel: any;
};

export type GridFetchResult<TData> = {
  rows: TData[];
  lastRow: number;
};

export type BaseAgGridToolbarHelpers<TData> = {
  api: GridApi<TData> | null;
  refresh: () => void;
};

export type BaseAgGridProps<TData> = Omit<
  AgGridReactProps<TData>,
  'columnDefs' | 'defaultColDef' | 'datasource' | 'rowModelType' | 'localeText' | 'onGridReady'
> & {
  columnDefs: ColDef<TData>[];
  fetchRows: (params: GridFetchParams) => Promise<GridFetchResult<TData> | null | undefined>;
  defaultColDef?: ColDef<TData>;
  localeText?: Record<string, string>;
  rowModelType?: 'infinite';
  onGridReady?: (event: GridReadyEvent<TData>) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  onFetchError?: (error: unknown) => void;
  title?: React.ReactNode;
  toolbar?: React.ReactNode | ((helpers: BaseAgGridToolbarHelpers<TData>) => React.ReactNode);
  toolbarLeft?: React.ReactNode | ((helpers: BaseAgGridToolbarHelpers<TData>) => React.ReactNode);
  toolbarRight?: React.ReactNode | ((helpers: BaseAgGridToolbarHelpers<TData>) => React.ReactNode);
  toolbarClassName?: string;
  toolbarStyle?: React.CSSProperties;
  themeClassName?: string;
  wrapperClassName?: string;
  wrapperStyle?: React.CSSProperties;
};

/**
 * Creates a reusable AG Grid wrapper that standardizes infinite row-model + pagination defaults.
 * It accepts the column definitions and a `fetchRows` function to load data (server-side/infinite).
 */
export function BaseAgGrid<TData>(props: BaseAgGridProps<TData>) {
  const {
    columnDefs,
    fetchRows,
    defaultColDef,
    onGridReady,
    onLoadingChange,
    onFetchError,
    title,
    toolbar,
    toolbarLeft,
    toolbarRight,
    toolbarClassName,
    toolbarStyle,
    themeClassName = 'ag-theme-alpine',
    wrapperClassName,
    wrapperStyle,
    rowModelType = 'infinite',
    cacheBlockSize = 20,
    maxBlocksInCache = 1,
    pagination = true,
    paginationPageSize = 20,
    rowSelection = 'multiple',
    rowHeight = 48,
    headerHeight = 48,
    animateRows = true,
    suppressColumnVirtualisation = true,
    suppressRowVirtualisation = true,
    localeText = defaultLocaleText,
    ...rest
  } = props;

  const [api, setApi] = useState<GridApi<TData> | null>(null);
  const fetchRowsRef = useRef(fetchRows);
  const onLoadingChangeRef = useRef(onLoadingChange);
  const onFetchErrorRef = useRef(onFetchError);

  useEffect(() => {
    fetchRowsRef.current = fetchRows;
  }, [fetchRows]);

  useEffect(() => {
    onLoadingChangeRef.current = onLoadingChange;
  }, [onLoadingChange]);

  useEffect(() => {
    onFetchErrorRef.current = onFetchError;
  }, [onFetchError]);

  const mergedWrapperClassName = useMemo(() => {
    return [themeClassName, wrapperClassName].filter(Boolean).join(' ');
  }, [themeClassName, wrapperClassName]);

  const resolvedDefaultColDef = useMemo<ColDef<TData>>(() => {
    if (defaultColDef) return defaultColDef;
    return {
      sortable: true,
      resizable: true,
      suppressMovable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
        maxNumConditions: 1,
      },
    } as ColDef<TData>;
  }, [defaultColDef]);

  const datasource = useMemo((): IDatasource => {
    return {
      getRows: async params => {
        try {
          onLoadingChangeRef.current?.(true);
          const result = await fetchRowsRef.current({
            startRow: params.startRow,
            endRow: params.endRow,
            filterModel: params.filterModel,
            sortModel: params.sortModel,
          });
          if (result) {
            params.successCallback(result.rows, result.lastRow);
          } else {
            params.failCallback();
          }
        } catch (error) {
          onFetchErrorRef.current?.(error);
          params.failCallback();
        } finally {
          onLoadingChangeRef.current?.(false);
        }
      },
    };
  }, []);

  /**
   * Refreshes the grid cache without forcing a re-render.
   */
  const refresh = useCallback(() => {
    api?.refreshInfiniteCache?.();
    api?.refreshServerSide?.();
  }, [api]);

  const toolbarHelpers = useMemo<BaseAgGridToolbarHelpers<TData>>(
    () => ({
      api,
      refresh,
    }),
    [api, refresh]
  );

  /**
   * Renders a toolbar slot that can be a ReactNode or a render function.
   */
  const renderToolbarSlot = useCallback(
    (slot?: React.ReactNode | ((helpers: BaseAgGridToolbarHelpers<TData>) => React.ReactNode)) => {
      if (!slot) return null;
      if (typeof slot === 'function') return slot(toolbarHelpers);
      return slot;
    },
    [toolbarHelpers]
  );

  const toolbarContent = useMemo(() => {
    const customToolbar = renderToolbarSlot(toolbar);
    if (customToolbar) return customToolbar;

    const left = renderToolbarSlot(toolbarLeft);
    const right = renderToolbarSlot(toolbarRight);
    if (!title && !left && !right) return null;

    const hasActionsRow = Boolean(left || right);
    const hasLeft = Boolean(left);
    const hasRight = Boolean(right);

    const toolbarRowClassName = [
      styles.toolbar,
      hasLeft && hasRight ? styles.toolbarSplit : '',
      !hasLeft && hasRight ? styles.toolbarRightOnly : '',
      hasLeft && !hasRight ? styles.toolbarLeftOnly : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={`${styles.toolbarContainer} ${toolbarClassName ?? ''}`} style={toolbarStyle}>
        {title ? (
          <div className={styles.titleRow}>
            <div className={styles.title}>{title}</div>
          </div>
        ) : null}
        {hasActionsRow ? (
          <div className={toolbarRowClassName}>
            {hasLeft ? <div className={styles.toolbarLeft}>{left}</div> : null}
            {hasRight ? <div className={styles.toolbarRight}>{right}</div> : null}
          </div>
        ) : null}
      </div>
    );
  }, [
    renderToolbarSlot,
    toolbar,
    toolbarClassName,
    toolbarLeft,
    toolbarRight,
    toolbarStyle,
    title,
  ]);

  /**
   * Bridges AG Grid's `onGridReady` to optional caller hooks.
   */
  const handleGridReady = useCallback(
    (event: GridReadyEvent<TData>) => {
      setApi(event.api);
      onGridReady?.(event);
    },
    [onGridReady]
  );

  return (
    <div className={styles.container} style={wrapperStyle ?? { height: '100%', width: '100%' }}>
      {toolbarContent}
      <div className={`${styles.gridWrapper} ${mergedWrapperClassName}`}>
        <AgGridReact<TData>
          rowModelType={rowModelType}
          cacheBlockSize={cacheBlockSize}
          maxBlocksInCache={maxBlocksInCache}
          datasource={datasource}
          columnDefs={columnDefs}
          defaultColDef={resolvedDefaultColDef}
          onGridReady={handleGridReady}
          pagination={pagination}
          paginationPageSize={paginationPageSize}
          rowSelection={rowSelection}
          rowHeight={rowHeight}
          headerHeight={headerHeight}
          animateRows={animateRows}
          suppressColumnVirtualisation={suppressColumnVirtualisation}
          suppressRowVirtualisation={suppressRowVirtualisation}
          localeText={localeText}
          {...rest}
        />
      </div>
    </div>
  );
}

export default BaseAgGrid;
